import React, { useEffect, useRef } from 'react';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import 'jsvectormap/dist/jsvectormap.css';

/** Red gradient from light to moderate dark red. */
export const getHeatMapColorForValue = (value, min, max) => {
  const span = max - min;
  const percent = span === 0 ? 0.5 : (value - min) / span;
  const r = Math.round(255 - percent * 116); // 255 → 139
  const g = Math.round(230 - percent * 230); // 230 → 0
  const b = Math.round(230 - percent * 230); // 230 → 0
  return `rgb(${r},${g},${b})`;
};

/**
 * Presentational world heatmap (jsvectormap regions + optional lat/lng markers).
 * @param {Array<{ code: string, name?: string, value?: number, lat?: number, lng?: number }>} items
 */
const HeatMap = ({ items = [], height = 500, mapId = 'iptv-heatmap' }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  const numericValues = items
    .filter(item => item.code && typeof item.value === 'number')
    .map(item => item.value);

  const minValue = numericValues.length ? Math.min(...numericValues) : 0;
  const maxValue = numericValues.length ? Math.max(...numericValues) : 0;
  const canRenderMap = numericValues.length > 0;

  const renderMap = () => {
    if (!mapRef.current || !canRenderMap) return;

    if (mapInstance.current) {
      mapInstance.current.destroy();
      mapInstance.current = null;
    }
    mapRef.current.innerHTML = '';
    document.querySelectorAll('.jvm-tooltip').forEach(el => el.remove());

    const theme =
      getComputedStyle(document.body).getPropertyValue('--bs-theme')?.trim() ||
      '#0d6efd';
    const inverse =
      getComputedStyle(document.body)
        .getPropertyValue('--bs-inverse')
        ?.trim() || '#999';
    const inverseRgb =
      getComputedStyle(document.body)
        .getPropertyValue('--bs-inverse-rgb')
        ?.trim() || '0,0,0';
    const bodyFont =
      getComputedStyle(document.body)
        .getPropertyValue('--bs-body-font-family')
        ?.trim() || 'inherit';

    const valuesForColor = {};
    const markers = [];
    const colorScale = [];
    const valueColorMap = {};

    items.forEach(item => {
      if (!item.code) return;
      const up = item.code.toUpperCase();
      const color = getHeatMapColorForValue(item.value, minValue, maxValue);

      valuesForColor[up] = item.value;

      if (!valueColorMap[item.value]) {
        valueColorMap[item.value] = color;
        colorScale.push(color);
      }

      if (item.lat != null && item.lng != null) {
        markers.push({
          name: `${item.name ?? item.code} (${item.value})`,
          coords: [item.lat, item.lng],
        });
      }
    });

    mapInstance.current = new jsVectorMap({
      selector: mapRef.current,
      map: 'world',
      backgroundColor: 'transparent',
      zoomButtons: true,
      zoomOnScroll: false,
      normalizeFunction: 'polynomial',
      focusOn: { x: 0.5, y: 0.5, scale: 1 },
      hoverOpacity: 1,
      showTooltip: true,

      series: {
        regions: [
          {
            scale: colorScale.length ? colorScale : [getHeatMapColorForValue(minValue, minValue, maxValue)],
            normalizeFunction: 'polynomial',
            values: valuesForColor,
            attribute: 'fill',
          },
        ],
      },

      markers,
      markerStyle: {
        initial: { fill: theme, stroke: 'none', r: 5 },
        hover: { fill: theme, stroke: 'none' },
      },

      labels: {
        regions: { render: () => null },
        markers: { render: () => null },
      },

      regionStyle: {
        initial: { fill: inverse, fillOpacity: 0.15, stroke: 'none' },
        hover: { fillOpacity: 0.5, cursor: 'pointer' },
      },

      onRegionTooltipShow(evt, tooltip) {
        evt.preventDefault();
        tooltip.text('', true);
      },
      onMarkerTooltipShow(evt, tooltip, idx) {
        tooltip.text(
          `<div style="text-align:center; padding:5px">${markers[idx].name}</div>`,
          true
        );
      },

      markerLabelStyle: {
        initial: {
          fontFamily: bodyFont,
          fontSize: '12px',
          fill: `rgba(${inverseRgb}, 0.75)`,
        },
      },
    });
  };

  useEffect(() => {
    if (!canRenderMap) {
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
      if (mapRef.current) mapRef.current.innerHTML = '';
      document.querySelectorAll('.jvm-tooltip').forEach(el => el.remove());
      return undefined;
    }

    renderMap();
    const onTheme = () => renderMap();
    document.addEventListener('theme-reload', onTheme);
    return () => {
      document.removeEventListener('theme-reload', onTheme);
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
    };
  }, [items, canRenderMap, minValue, maxValue]);

  return (
    <div>
      <div
        ref={mapRef}
        id={mapId}
        style={{ width: '100%', height: `${height}px` }}
      />

      {canRenderMap ? (
        <div className='mt-3 w-1/6'>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8 }}>{minValue}</span>
            <div
              style={{
                flexGrow: 1,
                height: 10,
                background: `linear-gradient(to right, ${getHeatMapColorForValue(
                  minValue,
                  minValue,
                  maxValue
                )}, ${getHeatMapColorForValue(maxValue, minValue, maxValue)})`,
                borderRadius: 4,
              }}
            />
            <span style={{ marginLeft: 8 }}>{maxValue}</span>
          </div>
          <div style={{ textAlign: 'center', fontSize: 12, marginTop: 4 }}>
            Low
            <span style={{ float: 'right' }}>High</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HeatMap;
